import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate, Link } from "react-router-dom";
import authService from "../../../services/authService";
import './styles.scss';

export default function ResetPassword() {
  const location = useLocation();
  const navigate = useNavigate();

  // Get email and verification code from previous page state
  const email = location.state?.email || "";
  const code = location.state?.code || "";

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [serverError, setServerError] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm({
    defaultValues: {
      password: "",
      confirmPassword: ""
    }
  });

  const password = watch("password");
  const confirmPassword = watch("confirmPassword");

  useEffect(() => {
    if (!email || !code) {
      navigate('/forgot-password', { replace: true });
    }
  }, [email, code, navigate]);

  const onSubmit = async (data) => {
    setServerError("");
    setIsLoading(true);

    try {
      await authService.resetPassword(email, code, data.password);

      navigate("/login", {
        state: { resetSuccess: true },
        replace: true,
      });
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.message ||
        "Failed to reset password. Please try again.";

      setServerError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <div className="reset-password-wrapper">
      <div className="reset-password-card ">
        <h1 className="reset-password-title">Password reset</h1>
        <p className="reset-password-subtitle">
         Kindly enter a new password. <strong>{email}</strong>
        </p>

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className="field">
            <label className="field-label">New Password</label>
            <div className="password-input-container">
              <input
                type={showPassword ? "text" : "password"}
                {...register("password", {
                  required: "Password is required",
                  minLength: {
                    value: 6,
                    message: "Password must be at least 6 characters"
                  },
                  onChange: () => setServerError("")
                })}
                disabled={isLoading}
                autoFocus
                className={`password-input ${errors.password ? 'field-input-error' : ''}`}
                placeholder="Password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={togglePasswordVisibility}
                disabled={isLoading}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
            {errors.password && (
              <span className="field-error">{errors.password.message}</span>
            )}
          </div>

          <div className="field">
            <label className="field-label">Verify Password</label>
            <div className="password-input-container">
              <input
                type={showConfirmPassword ? "text" : "password"}
                {...register("confirmPassword", {
                  required: "Please confirm your password",
                  validate: (value) => 
                    value === password || "Passwords do not match",
                  onChange: () => setServerError("")
                })}
                disabled={isLoading}
                className={`password-input ${errors.confirmPassword ? 'field-input-error' : ''}`}
                placeholder="Password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={toggleConfirmPasswordVisibility}
                disabled={isLoading}
              >
                {showConfirmPassword ? "Hide" : "Show"}
              </button>
            </div>
            {errors.confirmPassword && (
              <span className="field-error">{errors.confirmPassword.message}</span>
            )}
          </div>

          {/* Server error message */}
          {serverError && (
            <div className="form-error">
              {serverError}
            </div>
          )}

          <div className="field">
            <button type="submit" className="btn-submit" disabled={isLoading}>
              {isLoading ? "Saving..." : "Save"}
            </button>
          </div>

          <div className="back-to-login">
            <Link to="/verification-code" className="link-primary">
              ← Back to Verification Code
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}